import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import { useAuth } from '../hooks/useAuth'
import { usePractice } from '../hooks/usePractice'
import {
  getSettings, saveSettings, updateEmail, updatePassword,
  getSubscription, trialDaysRemaining, createBillingPortalSession,
  getPracticeMembers, getPracticeInvitations, getMyMembership,
  inviteMember, removeMember, updateMemberRole, cancelInvitation,
} from '../lib/supabase'
import { useToast } from '../components/Toast'
import usePageTitle from '../hooks/usePageTitle'

// ─── Constants ────────────────────────────────────────────────────────────────

const TOKENS = [
  { token: '{{client_name}}',     desc: 'Client\'s name' },
  { token: '{{bookkeeper_name}}', desc: 'Your display name' },
  { token: '{{request_title}}',   desc: 'Title of the request' },
  { token: '{{link}}',            desc: 'Upload link (rendered as a button in the email)' },
]

const DEFAULT_SUBJECT = 'Action required: receipts needed for {{request_title}}'
const DEFAULT_BODY = `Hi {{client_name}},

Your bookkeeper, {{bookkeeper_name}}, needs receipts or invoices for some recent transactions.

Please click the button below to see which transactions need a receipt and to upload your files:

{{link}}

It only takes a few minutes. You can upload a photo, PDF, or screenshot for each one. If you can't find a receipt, there is also an option to mark it as lost and provide a brief explanation.

Please complete this as soon as possible.

Thank you,
{{bookkeeper_name}}`

// ─── Reminder template defaults ───────────────────────────────────────────────
// Used when the practice hasn't customised the reminder. Same token set as
// the main request template, plus {{days_since_request}} which renders as
// "X days ago".

const DEFAULT_REMINDER_SUBJECT = 'Reminder: receipts still needed for {{request_title}}'
const DEFAULT_REMINDER_BODY = `Hi {{client_name}},

This is a friendly reminder that {{bookkeeper_name}} is still waiting on receipts for your "{{request_title}}" request, sent {{days_since_request}}.

{{link}}

If you have any questions about a specific transaction, or you've lost a receipt, you can either upload what you have or mark items as lost from the page above.

Thank you,
{{bookkeeper_name}}`

// Default schedule when the practice hasn't customised: 7 days after send.
const DEFAULT_REMINDER_DAYS = 7

function fillPreview(template, name) {
  return template
    .replace(/{{client_name}}/g, 'Jane Smith')
    .replace(/{{bookkeeper_name}}/g, name || 'Your Name')
    .replace(/{{request_title}}/g, 'Q1 2025 missing receipts')
    .replace(/{{link}}/g, 'https://receiptchaser.com/request/abc123')
}

// Reminder preview adds the {{days_since_request}} token, substituted
// with the actual configured schedule so the preview reflects what the
// client will see. Falls back to "7 days ago" if the number isn't a
// valid positive integer.
function fillReminderPreview(template, name, days) {
  const n = parseInt(days, 10)
  const daysLabel = Number.isFinite(n) && n > 0 ? `${n} days ago` : '7 days ago'
  return fillPreview(template, name).replace(/{{days_since_request}}/g, daysLabel)
}

// Shared preview shell for both the request and reminder email templates.
// Renders the same visual style as the real emails sent by the
// send-email edge function (teal #489291 header, Trebuchet font, button
// styling) so what you see here matches what arrives in the client's
// inbox. If the brand changes, update send-email/index.ts AND this
// component to keep them in sync.
function EmailPreview({ subject, body }) {
  return (
    <div style={{ marginTop: 24, border: '1.5px solid var(--surface-3)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', background: 'var(--surface-2)', fontSize: '0.78rem', color: 'var(--ink-muted)', borderBottom: '1px solid var(--surface-3)' }}>
        <strong>Subject:</strong> {subject}
      </div>
      <div style={{ background: '#f6f7f9', padding: 16 }}>
        <div style={{ background: '#489291', borderRadius: '8px 8px 0 0', padding: '16px 24px' }}>
          <span style={{
            fontFamily: "'Trebuchet MS', Verdana, Geneva, sans-serif",
            fontSize: '16px',
            color: 'white',
            fontWeight: 'bold',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}>
            Receipt Chaser
          </span>
        </div>
        <div style={{
          background: 'white',
          padding: '24px',
          borderRadius: '0 0 8px 8px',
          fontSize: '0.88rem',
          lineHeight: 1.7,
          color: '#0f1117',
          fontFamily: "'Trebuchet MS', Verdana, Geneva, sans-serif",
        }}>
          {body
            .split('\n')
            .filter(l => l.trim())
            .map((line, i) => {
              if (line.includes('https://receiptchaser.com/request/abc123')) {
                return (
                  <div key={i} style={{ margin: '16px 0' }}>
                    <span style={{ display: 'inline-block', padding: '10px 24px', background: '#489291', color: 'white', borderRadius: 6, fontSize: '0.88rem' }}>
                      Upload receipts →
                    </span>
                  </div>
                )
              }
              return <p key={i} style={{ margin: '0 0 12px' }}>{line}</p>
            })
          }
        </div>
      </div>
    </div>
  )
}

// ─── Nav items ────────────────────────────────────────────────────────────────

// Nav items are built dynamically in the Settings component based on role

// ─── Profile tab ──────────────────────────────────────────────────────────────

function ProfileTab({ user, isOwner, practiceName, setPracticeName, fullName, setFullName, phone, setPhone, website, setWebsite, onSaveProfile, onSaveName, savingProfile, savingName }) {
  return (
    <div>
      <h3 style={{ marginBottom: 4, fontSize: '1.1rem' }}>Profile</h3>
      <p style={{ color: 'var(--ink-muted)', fontSize: '0.88rem', marginBottom: 28 }}>
        {isOwner
          ? 'Your practice details and personal name. These appear in emails sent to your clients.'
          : 'Your personal name appears in emails you send. Practice details can only be edited by the owner.'}
      </p>

      {/* ── Personal name — editable by everyone ─────────────────────────────── */}
      <div style={{ maxWidth: 460, marginBottom: 28, paddingBottom: 28, borderBottom: '1px solid var(--surface-2)' }}>
        <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 12 }}>Your name</div>
        <div className="form-group" style={{ marginBottom: 16 }}>
          <label htmlFor="fullName">Full name</label>
          <input id="fullName" className="input" value={fullName}
            onChange={e => setFullName(e.target.value)}
            placeholder="e.g. Sarah Collins" />
        </div>
        <button className="btn btn-primary btn-sm" onClick={onSaveName} disabled={savingName}>
          {savingName ? 'Saving...' : 'Save name'}
        </button>
      </div>

      {/* ── Practice details — owner only ──────────────────────────────────── */}
      <div style={{ maxWidth: 460 }}>
        <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 12 }}>
          Practice details
          {!isOwner && (
            <span style={{ marginLeft: 8, fontSize: '0.75rem', fontWeight: 400, color: 'var(--ink-faint)' }}>
              — only the owner can edit these
            </span>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="practiceName">Practice name</label>
          <input id="practiceName" className="input" value={practiceName}
            onChange={e => setPracticeName(e.target.value)}
            placeholder="e.g. John Smith Bookkeeping"
            disabled={!isOwner} style={{ opacity: isOwner ? 1 : 0.6 }} />
        </div>
        <div className="form-group">
          <label htmlFor="phone">Phone number <span style={{ color: 'var(--ink-faint)', fontWeight: 400, textTransform: 'none', fontSize: '0.8rem' }}>(optional)</span></label>
          <input id="phone" type="tel" className="input" value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="e.g. 07700 900000"
            disabled={!isOwner} style={{ opacity: isOwner ? 1 : 0.6 }} />
        </div>
        <div className="form-group">
          <label htmlFor="website">Website <span style={{ color: 'var(--ink-faint)', fontWeight: 400, textTransform: 'none', fontSize: '0.8rem' }}>(optional)</span></label>
          <input id="website" type="url" className="input" value={website}
            onChange={e => setWebsite(e.target.value)}
            placeholder="e.g. https://johnsmithbookkeeping.co.uk"
            disabled={!isOwner} style={{ opacity: isOwner ? 1 : 0.6 }} />
        </div>
        <div className="form-group" style={{ marginBottom: 24 }}>
          <label>Account email</label>
          <input className="input" value={user?.email || ''} disabled style={{ opacity: 0.6 }} />
          <p style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginTop: 6 }}>
            To change your email address, go to Login &amp; security.
          </p>
        </div>

        {isOwner && (
          <button className="btn btn-primary" onClick={onSaveProfile} disabled={savingProfile}>
            {savingProfile ? 'Saving...' : 'Save practice details'}
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Login & security tab ─────────────────────────────────────────────────────

function LoginTab({ user, newEmail, setNewEmail, newPassword, setNewPassword, confirmPassword, setConfirmPassword, onChangeEmail, onChangePassword, savingEmail, savingPassword }) {
  return (
    <div>
      <h3 style={{ marginBottom: 4, fontSize: '1.1rem' }}>Login &amp; security</h3>
      <p style={{ color: 'var(--ink-muted)', fontSize: '0.88rem', marginBottom: 28 }}>
        Update your login credentials.
      </p>

      {/* Email */}
      <div style={{ paddingBottom: 28, marginBottom: 28, borderBottom: '1px solid var(--surface-2)' }}>
        <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 12 }}>Email address</div>
        <p style={{ fontSize: '0.8rem', color: 'var(--ink-faint)', marginBottom: 12 }}>
          A confirmation link will be sent to the new address before the change takes effect.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', maxWidth: 500 }}>
          <input
            type="email"
            className="input"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
          />
          <button className="btn btn-primary" onClick={onChangeEmail} disabled={savingEmail}>
            {savingEmail ? 'Sending…' : 'Change email'}
          </button>
        </div>
      </div>

      {/* Password */}
      <div>
        <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 12 }}>Password</div>
        <p style={{ fontSize: '0.8rem', color: 'var(--ink-faint)', marginBottom: 12 }}>
          Must be at least 8 characters.
        </p>
        <div style={{ display: 'grid', gap: 10, maxWidth: 360 }}>
          <input
            type="password"
            className="input"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            placeholder="New password"
            minLength={8}
          />
          <input
            type="password"
            className="input"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
          />
          <div>
            <button className="btn btn-primary" onClick={onChangePassword} disabled={savingPassword || !newPassword}>
              {savingPassword ? 'Updating…' : 'Change password'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Email template tab ───────────────────────────────────────────────────────

function EmailTab({ displayName, subject, setSubject, body, setBody, notifyOnComplete, setNotifyOnComplete, onSave, onReset, saving }) {
  const [showPreview, setShowPreview] = useState(false)

  return (
    <div>
      <h3 style={{ marginBottom: 4, fontSize: '1.1rem' }}>Email template</h3>
      <p style={{ color: 'var(--ink-muted)', fontSize: '0.88rem', marginBottom: 24 }}>
        Customise the email sent to clients when you share a receipt request.
      </p>

      {/* Token reference */}
      <div style={{
        background: 'var(--surface)', border: '1.5px solid var(--surface-3)',
        borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 24,
      }}>
        <div style={{ fontSize: '0.73rem', fontWeight: 700, color: 'var(--ink-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Available tokens
        </div>
        <div style={{ display: 'grid', gap: 7 }}>
          {TOKENS.map(t => (
            <div key={t.token} style={{ display: 'flex', alignItems: 'baseline', gap: 10, fontSize: '0.82rem' }}>
              <code style={{
                background: 'var(--white)', border: '1px solid var(--surface-3)',
                borderRadius: 4, padding: '1px 6px', fontFamily: 'monospace',
                fontSize: '0.78rem', color: 'var(--accent)', flexShrink: 0,
              }}>{t.token}</code>
              <span style={{ color: 'var(--ink-muted)' }}>{t.desc}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="subject">Subject line</label>
        <input
          id="subject"
          className="input"
          value={subject}
          onChange={e => setSubject(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label htmlFor="body">Email body</label>
        <p style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginBottom: 8 }}>
          Plain text — each paragraph is formatted automatically.
          <code style={{ fontSize: '0.78rem', marginLeft: 4 }}>{'{{link}}'}</code> becomes a button.
        </p>
        <textarea
          id="body"
          className="input"
          value={body}
          onChange={e => setBody(e.target.value)}
          rows={13}
          style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: '0.83rem', lineHeight: 1.7 }}
        />
      </div>

      {/* Notification toggle */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '14px 16px', background: 'var(--surface)',
          borderRadius: 'var(--radius)', marginBottom: 24, cursor: 'pointer',
        }}
        onClick={() => setNotifyOnComplete(v => !v)}
      >
        <div style={{
          width: 40, height: 22, borderRadius: 100, flexShrink: 0,
          background: notifyOnComplete ? 'var(--green)' : 'var(--surface-3)',
          position: 'relative', transition: 'background 0.2s',
        }}>
          <div style={{
            width: 16, height: 16, borderRadius: '50%', background: 'white',
            position: 'absolute', top: 3,
            left: notifyOnComplete ? 21 : 3,
            transition: 'left 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }} />
        </div>
        <div>
          <div style={{ fontSize: '0.88rem', fontWeight: 500 }}>Notify me when a client completes a request</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', marginTop: 2 }}>
            Receive an email when every transaction is uploaded or marked as lost.
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn btn-primary" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save email settings'}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowPreview(v => !v)}>
          {showPreview ? 'Hide preview' : '👁 Preview'}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={onReset} style={{ color: 'var(--ink-faint)' }}>
          Reset to default
        </button>
      </div>

      {/* Live preview */}
      {showPreview && (
        <EmailPreview
          subject={fillPreview(subject, displayName)}
          body={fillPreview(body, displayName)}
        />
      )}
    </div>
  )
}

// ─── Reminder tab ─────────────────────────────────────────────────────────────

// Reminder emails support an extra token compared to the main request email.
// {{days_since_request}} renders as "X days ago" in the body of the reminder.
const REMINDER_TOKENS = [
  { token: '{{client_name}}',         desc: 'Client\'s name' },
  { token: '{{bookkeeper_name}}',     desc: 'Your display name' },
  { token: '{{request_title}}',       desc: 'Title of the request' },
  { token: '{{link}}',                desc: 'Upload link (rendered as a button in the email)' },
  { token: '{{days_since_request}}',  desc: 'How long since the original request, e.g. "7 days ago"' },
]

function ReminderTab({
  displayName,
  remindersEnabled, setRemindersEnabled,
  reminderDays, setReminderDays,
  reminderSubject, setReminderSubject,
  reminderBody, setReminderBody,
  onSave, onReset, saving,
}) {
  const [showPreview, setShowPreview] = useState(false)

  return (
    <div>
      <h3 style={{ marginBottom: 4, fontSize: '1.1rem' }}>Auto-reminders</h3>
      <p style={{ color: 'var(--ink-muted)', fontSize: '0.88rem', marginBottom: 24 }}>
        Send a single reminder email to clients who haven't completed their request after a set number of days.
      </p>

      {/* Master on/off */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '14px 16px', background: 'var(--surface)',
          borderRadius: 'var(--radius)', marginBottom: 24, cursor: 'pointer',
        }}
        onClick={() => setRemindersEnabled(v => !v)}
      >
        <div style={{
          width: 40, height: 22, borderRadius: 100, flexShrink: 0,
          background: remindersEnabled ? 'var(--green)' : 'var(--surface-3)',
          position: 'relative', transition: 'background 0.2s',
        }}>
          <div style={{
            width: 16, height: 16, borderRadius: '50%', background: 'white',
            position: 'absolute', top: 3,
            left: remindersEnabled ? 21 : 3,
            transition: 'left 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }} />
        </div>
        <div>
          <div style={{ fontSize: '0.88rem', fontWeight: 500 }}>Send auto-reminders for incomplete requests</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', marginTop: 2 }}>
            One reminder per request, sent after the schedule below. Individual clients and requests can opt out separately.
          </div>
        </div>
      </div>

      {/* Everything below is disabled (visually dimmed) when reminders are off,
          but still editable so the user can prepare a template ready to enable. */}
      <div style={{ opacity: remindersEnabled ? 1 : 0.55, transition: 'opacity 0.15s' }}>

        <div className="form-group">
          <label htmlFor="reminder-days">Send the reminder after</label>
          <p style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginBottom: 8 }}>
            Number of days since the original request was sent.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              id="reminder-days"
              className="input"
              type="number"
              min={1}
              max={90}
              value={reminderDays}
              onChange={e => setReminderDays(e.target.value)}
              style={{ width: 100 }}
            />
            <span style={{ fontSize: '0.9rem', color: 'var(--ink-muted)' }}>
              days after the request was sent
            </span>
          </div>
        </div>

        {/* Token reference */}
        <div style={{
          background: 'var(--surface)', border: '1.5px solid var(--surface-3)',
          borderRadius: 'var(--radius)', padding: '14px 16px',
          marginTop: 20, marginBottom: 20,
        }}>
          <div style={{ fontSize: '0.73rem', fontWeight: 700, color: 'var(--ink-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Available tokens
          </div>
          <div style={{ display: 'grid', gap: 7 }}>
            {REMINDER_TOKENS.map(t => (
              <div key={t.token} style={{ display: 'flex', alignItems: 'baseline', gap: 10, fontSize: '0.82rem' }}>
                <code style={{
                  background: 'var(--white)', border: '1px solid var(--surface-3)',
                  borderRadius: 4, padding: '1px 6px', fontFamily: 'monospace',
                  fontSize: '0.78rem', color: 'var(--accent)', flexShrink: 0,
                }}>{t.token}</code>
                <span style={{ color: 'var(--ink-muted)' }}>{t.desc}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="reminder-subject">Subject line</label>
          <input
            id="reminder-subject"
            className="input"
            value={reminderSubject}
            onChange={e => setReminderSubject(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="reminder-body">Email body</label>
          <p style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginBottom: 8 }}>
            Plain text. Each paragraph is formatted automatically.{' '}
            <code style={{ fontSize: '0.78rem', marginLeft: 4 }}>{'{{link}}'}</code> becomes a button.
          </p>
          <textarea
            id="reminder-body"
            className="input"
            value={reminderBody}
            onChange={e => setReminderBody(e.target.value)}
            rows={12}
            style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: '0.83rem', lineHeight: 1.7 }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn btn-primary" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save reminder settings'}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowPreview(v => !v)}>
          {showPreview ? 'Hide preview' : '👁 Preview'}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={onReset} style={{ color: 'var(--ink-faint)' }}>
          Reset to default
        </button>
      </div>

      {/* Live preview */}
      {showPreview && (
        <EmailPreview
          subject={fillReminderPreview(reminderSubject, displayName, reminderDays)}
          body={fillReminderPreview(reminderBody, displayName, reminderDays)}
        />
      )}
    </div>
  )
}

// ─── Billing tab ──────────────────────────────────────────────────────────────

function BillingTab({ sub, clientCount }) {
  const toast = useToast()
  const [portalLoading, setPortalLoading] = useState(false)

  const handleManageBilling = async () => {
    setPortalLoading(true)
    const { error } = await createBillingPortalSession()
    setPortalLoading(false)
    if (error) toast(error.message || 'Could not open billing portal', 'error')
  }

  const statusLabel = {
    trialing:  'Free trial',
    active:    'Active',
    past_due:  'Payment overdue',
    canceled:  'Cancelled',
    none:      'No subscription',
  }

  const tierLabel = {
    per_client: 'Per client — £3/client/month',
    pro:        'Pro — £25/month unlimited',
  }

  const daysLeft  = trialDaysRemaining(sub)
  // isCancelling covers two cases:
  // 1. Active with cancel_at_period_end=true (Stripe marks it before period ends)
  // 2. Status is 'canceled' but current_period_ends_at is in the future
  const canceledWithAccess = sub?.subscription_status === 'canceled' &&
    sub?.current_period_ends_at && new Date(sub.current_period_ends_at) > new Date()
  const isCancelling = (sub?.cancel_at_period_end && sub?.subscription_status === 'active') || canceledWithAccess

  const formatDate = (iso) => {
    if (!iso) return null
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
  }

  return (
    <div>
      <h3 style={{ marginBottom: 4, fontSize: '1.1rem' }}>Billing</h3>
      <p style={{ color: 'var(--ink-muted)', fontSize: '0.88rem', marginBottom: 28 }}>
        Your current plan, usage, and invoices.
      </p>

      <div style={{ display: 'grid', gap: 14, marginBottom: 28 }}>

        {/* Plan + status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1.5px solid var(--surface-3)', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Plan</div>
            <div style={{ fontWeight: 500 }}>
              {sub?.subscription_tier ? tierLabel[sub.subscription_tier] : 'No plan selected'}
            </div>
          </div>
          <span style={{
            padding: '3px 12px', borderRadius: 100, fontSize: '0.75rem', fontWeight: 700,
            background: isCancelling ? '#fee2e2' :
                        sub?.subscription_status === 'active'   ? 'var(--green-light)' :
                        sub?.subscription_status === 'trialing' ? 'var(--blue-light)'  :
                        sub?.subscription_status === 'past_due' ? '#fee2e2' : 'var(--surface-2)',
            color: isCancelling ? '#dc2626' :
                   sub?.subscription_status === 'active'   ? 'var(--green)' :
                   sub?.subscription_status === 'trialing' ? 'var(--blue)'  :
                   sub?.subscription_status === 'past_due' ? '#dc2626' : 'var(--ink-muted)',
          }}>
            {isCancelling ? 'Cancels at period end' : (statusLabel[sub?.subscription_status] || 'Unknown')}
          </span>
        </div>

        {/* Cancellation notice */}
        {isCancelling && sub?.current_period_ends_at && (
          <div style={{ padding: '14px 18px', background: '#fee2e2', borderRadius: 'var(--radius)', border: '1.5px solid #fca5a5', fontSize: '0.88rem', color: '#dc2626' }}>
            ⚠ Your subscription is cancelled and will end on <strong>{formatDate(sub.current_period_ends_at)}</strong>. You'll keep full access until then.
          </div>
        )}

        {/* Trial notice */}
        {sub?.subscription_status === 'trialing' && (
          <div style={{ padding: '14px 18px', background: 'var(--blue-light)', borderRadius: 'var(--radius)', border: '1.5px solid var(--blue)', fontSize: '0.88rem', color: 'var(--blue)' }}>
            {daysLeft > 0
              ? <>⏳ <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong> remaining in your free trial.</>
              : <>Your free trial has ended.</>
            }
            {' '}<Link to="/pricing/account" style={{ color: 'var(--blue)', fontWeight: 600 }}>Choose a plan →</Link>
          </div>
        )}

        {/* Per-client usage */}
        {sub?.subscription_tier === 'per_client' && sub?.subscription_status === 'active' && (
          <div style={{ padding: '14px 18px', background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1.5px solid var(--surface-3)' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Current usage</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', lineHeight: 1 }}>{clientCount}</span>
              <span style={{ color: 'var(--ink-muted)', fontSize: '0.88rem' }}>
                active client{clientCount !== 1 ? 's' : ''} · <strong>£{clientCount * 3}/month</strong> at next billing date
              </span>
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginTop: 8, marginBottom: 0 }}>
              Your bill is calculated from your active client count on the 28th of each month. Archived clients are excluded.
            </p>
          </div>
        )}

        {/* Next billing / expiry date */}
        {sub?.current_period_ends_at && sub?.subscription_status === 'active' && (
          <div style={{ padding: '14px 18px', background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1.5px solid var(--surface-3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                {isCancelling ? 'Access ends' : 'Next billing date'}
              </div>
              <div style={{ fontWeight: 500 }}>{formatDate(sub.current_period_ends_at)}</div>
            </div>
          </div>
        )}

        {/* Past due notice */}
        {sub?.subscription_status === 'past_due' && (
          <div style={{ padding: '14px 18px', background: '#fee2e2', borderRadius: 'var(--radius)', border: '1.5px solid #fca5a5', fontSize: '0.88rem', color: '#dc2626' }}>
            ⚠ Your last payment failed. Please update your payment method to avoid losing access.
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {/* Stripe Billing Portal — cancel, invoices, payment method */}
        {sub?.stripe_customer_id && (
          <button
            className="btn btn-secondary"
            onClick={handleManageBilling}
            disabled={portalLoading}
          >
            {portalLoading ? 'Opening…' : '💳 Manage subscription & invoices'}
          </button>
        )}
        {/* Change plan — visible for all subscription states */}
        {sub?.subscription_status === 'active' && !isCancelling && (
          <Link to="/pricing/account" className="btn btn-secondary">
            ↕ Change plan
          </Link>
        )}
        {(sub?.subscription_status === 'trialing' || !sub?.subscription_tier) && (
          <Link to="/pricing/account" className="btn btn-primary">
            Choose a plan →
          </Link>
        )}
        {isCancelling && (
          <Link to="/pricing/account" className="btn btn-primary">
            Resubscribe →
          </Link>
        )}
        {sub?.subscription_status === 'past_due' && (
          <Link to="/pricing/account" className="btn btn-primary">
            Update payment details →
          </Link>
        )}
      </div>

      <p style={{ marginTop: 16, fontSize: '0.78rem', color: 'var(--ink-faint)' }}>
        The billing portal lets you cancel your subscription, update your payment method, and download past invoices.
      </p>
    </div>
  )
}

// ─── Team tab ─────────────────────────────────────────────────────────────────

function TeamTab({ userId }) {
  const toast = useToast()
  const [members, setMembers]         = useState([])
  const [invitations, setInvitations] = useState([])
  const [membership, setMembership]   = useState(null)
  const [loading, setLoading]         = useState(true)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole]   = useState('assistant')
  const [inviting, setInviting]       = useState(false)
  const [practiceName, setPracticeName] = useState('')

  const load = async () => {
    const [{ data: myMem }, { data: settings }] = await Promise.all([
      getMyMembership(),
      getSettings(userId),
    ])
    setMembership(myMem)
    setPracticeName(settings?.practice_name || '')
    if (myMem?.practice_id) {
      const [{ data: memberList }, { data: invList }] = await Promise.all([
        getPracticeMembers(myMem.practice_id),
        getPracticeInvitations(myMem.practice_id),
      ])
      setMembers(memberList || [])
      setInvitations(invList || [])
    }
    setLoading(false)
  }

  useEffect(() => { load() }, [userId])

  const handleInvite = async (e) => {
    e.preventDefault()
    if (!inviteEmail.trim()) return
    setInviting(true)
    const { error } = await inviteMember(inviteEmail.trim(), inviteRole, practiceName)
    setInviting(false)
    if (error) { toast(error.message, 'error') }
    else { toast(`Invitation sent to ${inviteEmail} ✓`, 'success'); setInviteEmail(''); load() }
  }

  const handleRemove = async (member) => {
    if (!window.confirm(`Remove ${member.email} from your practice? They will lose access immediately.`)) return
    const { error } = await removeMember(member.id)
    if (error) { toast(error.message, 'error') }
    else { toast(`${member.email} removed`, 'default'); load() }
  }

  const handleChangeRole = async (member, newRole) => {
    const { error } = await updateMemberRole(member.id, newRole)
    if (error) { toast(error.message, 'error') }
    else { toast('Role updated ✓', 'success'); load() }
  }

  const handleCancelInvite = async (inv) => {
    const { error } = await cancelInvitation(inv.id)
    if (error) { toast(error.message, 'error') }
    else { toast('Invitation cancelled', 'default'); load() }
  }

  const isOwner = membership?.role === 'owner'

  const ROLE_BADGE = {
    owner:     { bg: 'var(--accent-light)',  color: 'var(--accent)',    label: 'Owner' },
    admin:     { bg: 'var(--blue-light)',     color: 'var(--blue)',      label: 'Admin' },
    assistant: { bg: 'var(--surface-2)',      color: 'var(--ink-muted)', label: 'Assistant' },
  }

  if (loading) return <div className="spinner" style={{ margin: '40px auto' }} />

  return (
    <div>
      <h3 style={{ marginBottom: 4, fontSize: '1.1rem' }}>Team</h3>
      <p style={{ color: 'var(--ink-muted)', fontSize: '0.88rem', marginBottom: 28 }}>
        Manage who has access to your practice.
      </p>

      {/* Role legend */}
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: '14px 18px', marginBottom: 24, fontSize: '0.82rem' }}>
        <div style={{ fontWeight: 700, color: 'var(--ink-muted)', marginBottom: 10, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role permissions</div>
        <div style={{ display: 'grid', gap: 6 }}>
          {[
            { role: 'Owner',     desc: 'Full access including billing and team management' },
            { role: 'Admin',     desc: 'Full access except billing and team management' },
            { role: 'Assistant', desc: 'Can view, upload and add notes. Cannot delete anything' },
          ].map(r => (
            <div key={r.role} style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
              <span style={{ fontWeight: 600, minWidth: 72 }}>{r.role}</span>
              <span style={{ color: 'var(--ink-muted)' }}>{r.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Current members */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 12 }}>Current members ({members.length})</div>
        <div style={{ display: 'grid', gap: 8 }}>
          {members.map(member => {
            const badge = ROLE_BADGE[member.role] || ROLE_BADGE.assistant
            const isMe = member.user_id === userId
            return (
              <div key={member.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1.5px solid var(--surface-3)', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: badge.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 700, color: badge.color }}>
                    {(member.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 500 }}>{member.email}{isMe && <span style={{ fontSize: '0.75rem', color: 'var(--ink-faint)', marginLeft: 6 }}>(you)</span>}</div>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '1px 8px', borderRadius: 100, background: badge.bg, color: badge.color }}>{badge.label}</span>
                  </div>
                </div>
                {isOwner && !isMe && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select
                      className="input"
                      value={member.role}
                      onChange={e => handleChangeRole(member, e.target.value)}
                      style={{ fontSize: '0.82rem', padding: '4px 8px', height: 'auto' }}
                    >
                      <option value="admin">Admin</option>
                      <option value="assistant">Assistant</option>
                    </select>
                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleRemove(member)} style={{ color: '#dc2626' }} title="Remove member">🗑</button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 12 }}>Pending invitations ({invitations.length})</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {invitations.map(inv => (
              <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1.5px dashed var(--surface-3)', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 500 }}>{inv.invitee_email}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)' }}>
                    Invited as {inv.role === 'admin' ? 'Admin' : 'Assistant'} · expires {new Date(inv.expires_at).toLocaleDateString('en-GB')}
                  </div>
                </div>
                {isOwner && (
                  <button className="btn btn-ghost btn-sm" onClick={() => handleCancelInvite(inv)} style={{ color: 'var(--ink-muted)' }}>
                    Cancel
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite form — owner only */}
      {isOwner && (
        <div style={{ borderTop: '1.5px solid var(--surface-2)', paddingTop: 24 }}>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 16 }}>Invite a team member</div>
          <form onSubmit={handleInvite}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
                <label htmlFor="inviteEmail">Email address</label>
                <input
                  id="inviteEmail"
                  type="email"
                  className="input"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="colleague@example.com"
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="inviteRole">Role</label>
                <select id="inviteRole" className="input" value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                  <option value="admin">Admin</option>
                  <option value="assistant">Assistant</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary" disabled={inviting} style={{ marginBottom: 0 }}>
                {inviting ? 'Sending...' : 'Send invite'}
              </button>
            </div>
          </form>
          <p style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginTop: 10 }}>
            They will receive an email with a link to create their account and join your practice.
          </p>
        </div>
      )}

      {!isOwner && (
        <p style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', padding: '14px 18px', background: 'var(--surface)', borderRadius: 'var(--radius)' }}>
          Only the practice owner can invite or manage team members.
        </p>
      )}
    </div>
  )
}

// ─── Main settings page ───────────────────────────────────────────────────────

export default function Settings() {
  const { user } = useAuth()
  const { practiceId, role } = usePractice()
  const toast    = useToast()
  usePageTitle('Settings')
  const [activeTab, setActiveTab] = useState('profile')
  const [loading, setLoading]     = useState(true)

  // Profile
  const [practiceName, setPracticeName] = useState('')
  const [fullName, setFullName]         = useState('')
  const [phone, setPhone]               = useState('')
  const [website, setWebsite]           = useState('')

  // Login
  const [newEmail, setNewEmail]               = useState('')
  const [newPassword, setNewPassword]         = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Email template
  const [subject, setSubject]                   = useState(DEFAULT_SUBJECT)
  const [body, setBody]                         = useState(DEFAULT_BODY)
  const [notifyOnComplete, setNotifyOnComplete] = useState(true)

  // Reminder template & schedule
  const [remindersEnabled, setRemindersEnabled] = useState(true)
  const [reminderDays, setReminderDays]         = useState(String(DEFAULT_REMINDER_DAYS))
  const [reminderSubject, setReminderSubject]   = useState(DEFAULT_REMINDER_SUBJECT)
  const [reminderBody, setReminderBody]         = useState(DEFAULT_REMINDER_BODY)

  // Subscription / billing
  const [sub, setSub]                 = useState(null)
  const [clientCount, setClientCount] = useState(0)

  // Saving
  const [savingProfile, setSavingProfile]   = useState(false)
  const [savingName, setSavingName]         = useState(false)
  const [savingEmail, setSavingEmail]       = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [savingReminders, setSavingReminders] = useState(false)

  const isOwner = role === 'owner'

  // Only show billing tab to owners
  const navItems = [
    { id: 'profile',   label: 'Profile',          icon: '👤' },
    { id: 'login',     label: 'Login & security', icon: '🔒' },
    { id: 'email',     label: 'Email template',   icon: '✉️' },
    { id: 'reminders', label: 'Auto-reminders',   icon: '⏰' },
    { id: 'team',      label: 'Team',             icon: '👥' },
    ...(isOwner ? [{ id: 'billing', label: 'Billing', icon: '💳' }] : []),
  ]

  useEffect(() => {
    if (!user || !practiceId) return
    setNewEmail(user.email || '')

    // Load practice-level settings from the owner's row
    const practiceLoad = getSettings(practiceId).then(({ data }) => {
      if (data) {
        setPracticeName(data.practice_name || '')
        setPhone(data.phone || '')
        setWebsite(data.website || '')
        setSubject(data.email_subject_template || DEFAULT_SUBJECT)
        setBody(data.email_body_template || DEFAULT_BODY)
        setNotifyOnComplete(data.notify_on_completion !== false)
        setClientCount(data.client_count || 0)
        // Reminder fields. Treat undefined as "use default" so this loader
        // works against rows that were created before the columns existed.
        setRemindersEnabled(data.reminders_enabled !== false)
        setReminderDays(String(data.reminder_days_after_send ?? DEFAULT_REMINDER_DAYS))
        setReminderSubject(data.reminder_subject_template || DEFAULT_REMINDER_SUBJECT)
        setReminderBody(data.reminder_body_template || DEFAULT_REMINDER_BODY)
      }
    })

    // Load each user's own full_name from their own settings row
    const nameLoad = getSettings(user.id).then(({ data }) => {
      setFullName(data?.full_name || '')
    })

    Promise.all([practiceLoad, nameLoad]).then(() => setLoading(false))

    // Billing is always tied to the practice owner
    getSubscription(practiceId).then(({ data }) => setSub(data || null))
  }, [user, practiceId])

  // Save user's own name — keyed to user.id, available to all roles
  const handleSaveName = async () => {
    setSavingName(true)
    const { error } = await saveSettings(user.id, {
      full_name: fullName.trim(),
    })
    setSavingName(false)
    if (error) toast(error.message, 'error')
    else toast('Name saved ✓', 'success')
  }

  // Save practice details — keyed to practiceId, owner only
  const handleSaveProfile = async () => {
    setSavingProfile(true)
    const { error } = await saveSettings(practiceId, {
      practice_name: practiceName.trim(),
      phone:         phone.trim(),
      website:       website.trim(),
    })
    setSavingProfile(false)
    if (error) toast(error.message, 'error')
    else toast('Practice details saved ✓', 'success')
  }

  const handleChangeEmail = async () => {
    if (newEmail === user.email) { toast('That\'s already your email address', 'error'); return }
    setSavingEmail(true)
    const { error } = await updateEmail(newEmail)
    setSavingEmail(false)
    if (error) toast(error.message, 'error')
    else toast('Confirmation sent to your new address — check your inbox', 'success')
  }

  const handleChangePassword = async () => {
    if (newPassword.length < 8) { toast('Password must be at least 8 characters', 'error'); return }
    if (newPassword !== confirmPassword) { toast('Passwords do not match', 'error'); return }
    setSavingPassword(true)
    const { error } = await updatePassword(newPassword)
    setSavingPassword(false)
    if (error) toast(error.message, 'error')
    else { toast('Password updated ✓', 'success'); setNewPassword(''); setConfirmPassword('') }
  }

  const handleSaveTemplate = async () => {
    if (!subject.includes('{{link}}') && !body.includes('{{link}}')) {
      toast('Your template must include {{link}} so clients receive the upload link', 'error')
      return
    }
    setSavingTemplate(true)
    const { error } = await saveSettings(practiceId, {
      email_subject_template: subject.trim(),
      email_body_template:    body.trim(),
      notify_on_completion:   notifyOnComplete,
    })
    setSavingTemplate(false)
    if (error) toast(error.message, 'error')
    else toast('Email settings saved ✓', 'success')
  }

  const handleResetTemplate = () => {
    setSubject(DEFAULT_SUBJECT)
    setBody(DEFAULT_BODY)
    toast('Template reset to default', 'default')
  }

  // ─── Reminder template save / reset ─────────────────────────────────────────
  const handleSaveReminders = async () => {
    // Validate the day count is a sensible integer
    const daysNum = parseInt(reminderDays, 10)
    if (Number.isNaN(daysNum) || daysNum < 1 || daysNum > 90) {
      toast('Reminder schedule must be between 1 and 90 days', 'error')
      return
    }
    // The link token must appear somewhere in the reminder, or the email is
    // useless (the client can't get to the upload page).
    if (!reminderSubject.includes('{{link}}') && !reminderBody.includes('{{link}}')) {
      toast('Your reminder template must include {{link}} so clients receive the upload link', 'error')
      return
    }
    setSavingReminders(true)
    const { error } = await saveSettings(practiceId, {
      reminders_enabled:           remindersEnabled,
      reminder_days_after_send:    daysNum,
      reminder_subject_template:   reminderSubject.trim(),
      reminder_body_template:      reminderBody.trim(),
    })
    setSavingReminders(false)
    if (error) toast(error.message, 'error')
    else toast('Reminder settings saved ✓', 'success')
  }

  const handleResetReminders = () => {
    setReminderDays(String(DEFAULT_REMINDER_DAYS))
    setReminderSubject(DEFAULT_REMINDER_SUBJECT)
    setReminderBody(DEFAULT_REMINDER_BODY)
    toast('Reminder template reset to default', 'default')
  }

  if (loading) return (
    <>
      <Navbar />
      <div className="loading-screen"><div className="spinner" /></div>
    </>
  )

  return (
    <>
      <Navbar />
      <main className="container" style={{ padding: '40px 24px' }}>

        <div className="fade-up" style={{ marginBottom: 32 }}>
          <h2 style={{ marginBottom: 4 }}>Settings</h2>
          <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem' }}>Manage your account and email preferences.</p>
        </div>

        <div className="fade-up-1" style={{ display: 'flex', gap: 32, alignItems: 'flex-start' }}>

          {/* ── Left sidebar nav ────────────────────────────────────────────── */}
          <div style={{
            width: 200, flexShrink: 0,
            background: 'var(--white)',
            border: '1.5px solid var(--surface-2)',
            borderRadius: 'var(--radius-lg)',
            padding: 8,
            position: 'sticky', top: 80,
          }}>
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                style={{
                  width: '100%', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', borderRadius: 'var(--radius)',
                  border: 'none', cursor: 'pointer',
                  fontFamily: 'var(--font-body)', fontSize: '0.88rem',
                  fontWeight: activeTab === item.id ? 600 : 400,
                  background: activeTab === item.id ? 'var(--accent-light)' : 'transparent',
                  color: activeTab === item.id ? 'var(--accent)' : 'var(--ink-muted)',
                  transition: 'all 0.15s',
                  marginBottom: 2,
                }}
              >
                <span style={{ fontSize: '1rem' }}>{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>

          {/* ── Content panel ───────────────────────────────────────────────── */}
          <div className="card" style={{ flex: 1, padding: 32, minWidth: 0 }}>
            {activeTab === 'profile' && (
              <ProfileTab
                user={user}
                isOwner={isOwner}
                practiceName={practiceName}
                setPracticeName={setPracticeName}
                fullName={fullName}
                setFullName={setFullName}
                phone={phone}
                setPhone={setPhone}
                website={website}
                setWebsite={setWebsite}
                onSaveProfile={handleSaveProfile}
                onSaveName={handleSaveName}
                savingProfile={savingProfile}
                savingName={savingName}
              />
            )}
            {activeTab === 'login' && (
              <LoginTab
                user={user}
                newEmail={newEmail}
                setNewEmail={setNewEmail}
                newPassword={newPassword}
                setNewPassword={setNewPassword}
                confirmPassword={confirmPassword}
                setConfirmPassword={setConfirmPassword}
                onChangeEmail={handleChangeEmail}
                onChangePassword={handleChangePassword}
                savingEmail={savingEmail}
                savingPassword={savingPassword}
              />
            )}
            {activeTab === 'email' && (
              <EmailTab
                displayName={fullName ? (practiceName ? `${fullName} at ${practiceName}` : fullName) : practiceName}
                subject={subject}
                setSubject={setSubject}
                body={body}
                setBody={setBody}
                notifyOnComplete={notifyOnComplete}
                setNotifyOnComplete={setNotifyOnComplete}
                onSave={handleSaveTemplate}
                onReset={handleResetTemplate}
                saving={savingTemplate}
              />
            )}
            {activeTab === 'reminders' && (
              <ReminderTab
                displayName={fullName ? (practiceName ? `${fullName} at ${practiceName}` : fullName) : practiceName}
                remindersEnabled={remindersEnabled}
                setRemindersEnabled={setRemindersEnabled}
                reminderDays={reminderDays}
                setReminderDays={setReminderDays}
                reminderSubject={reminderSubject}
                setReminderSubject={setReminderSubject}
                reminderBody={reminderBody}
                setReminderBody={setReminderBody}
                onSave={handleSaveReminders}
                onReset={handleResetReminders}
                saving={savingReminders}
              />
            )}
            {activeTab === 'team' && (
              <TeamTab userId={user.id} />
            )}
            {activeTab === 'billing' && (
              <BillingTab sub={sub} clientCount={clientCount} />
            )}
          </div>

        </div>
      </main>
    </>
  )
}
